import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import Layout from 'layouts/AppLayout';
import AdminCommandLayout from 'layouts/AdminCommandLayout';
import { hasPermission as checkPermission } from 'shared/utils/permissions';

/**
 * ProtectedRoute — Permission-Driven
 *
 * Wraps any page that requires authentication.
 *
 * Access check priority:
 *   1. If `requiredPermission` is provided → check permissions (primary).
 *   2. Else if `requiredRole` is provided  → fallback to legacy role check.
 *   3. If neither provided                 → only authentication required.
 *
 * Layout selection:
 *   - `layout="command"` → AdminCommandLayout (dark cockpit sidebar)
 *   - default            → standard AppLayout
 */
const ProtectedRoute = ({ children, requiredRole, requiredPermission, layout }) => {
  const { isAuthenticated, hasRole, loading, user } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // ── Permission check (primary) ─────────────────────────────
  if (requiredPermission) {
    const perms = Array.isArray(requiredPermission)
      ? requiredPermission
      : [requiredPermission];

    const hasAccess = checkPermission(user, perms);
    if (hasAccess) {
      return wrapInLayout(layout, children);
    }

    // If permission check fails, still try legacy role as fallback
    if (requiredRole) {
      const hasRequiredRole = Array.isArray(requiredRole)
        ? requiredRole.some((role) => hasRole(role))
        : hasRole(requiredRole);

      if (hasRequiredRole) {
        return wrapInLayout(layout, children);
      }
    }

    return wrapInLayout(layout, <AccessDenied requiredPermission={requiredPermission} />);
  }

  // ── Legacy role check (fallback) ───────────────────────────
  if (requiredRole) {
    const hasRequiredRole = Array.isArray(requiredRole)
      ? requiredRole.some((role) => hasRole(role))
      : hasRole(requiredRole);

    if (!hasRequiredRole) {
      return wrapInLayout(layout, <AccessDenied requiredRole={requiredRole} user={user} />);
    }
  }

  return wrapInLayout(layout, children);
};

// ─── Layout wrapper helper ───────────────────────────────────
function wrapInLayout(layout, children) {
  if (layout === 'command') {
    return <AdminCommandLayout>{children}</AdminCommandLayout>;
  }
  return <Layout>{children}</Layout>;
}

// ─── Access Denied component ─────────────────────────────────
function AccessDenied({ requiredPermission, requiredRole, user }) {
  return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <h2 style={{ color: '#d32f2f' }}>⛔ Access Denied</h2>
      <p style={{ color: '#666', marginTop: 12 }}>
        You don&apos;t have the required permission to access this page.
      </p>
      {requiredPermission && (
        <p style={{ color: '#999', fontSize: '0.85rem', marginTop: 8 }}>
          Required: <code>{Array.isArray(requiredPermission) ? requiredPermission.join(' or ') : requiredPermission}</code>
        </p>
      )}
      {requiredRole && (
        <>
          <p style={{ color: '#999', fontSize: '0.85rem', marginTop: 8 }}>
            Required role: {Array.isArray(requiredRole) ? requiredRole.join(' or ') : requiredRole}
          </p>
          <p style={{ color: '#999', fontSize: '0.85rem' }}>
            Your role: {user?.role || 'Unknown'}
          </p>
        </>
      )}
      <button
        onClick={() => window.history.back()}
        style={{
          marginTop: 20,
          padding: '8px 24px',
          background: '#1976d2',
          color: 'white',
          border: 'none',
          borderRadius: 4,
          cursor: 'pointer',
        }}
      >
        Go Back
      </button>
    </div>
  );
}

export default ProtectedRoute;
